import { apiClient } from '@/lib/api-client';
import type {
  GrantUserOrgAccessRequest,
  GrantUserOrgAccessResponse,
  RevokeUserOrgAccessRequest,
  RevokeUserOrgAccessResponse,
} from '@/types';

/**
 * Grant a user platform-wide SuperAdmin access.
 *
 * Calls the platform-level endpoint `POST /users/{user_id}/grant-super-admin`.
 * This is the correct endpoint to use since SuperAdmin is a platform-level concept
 * and is no longer granted via the org-scoped `POST /orgs/{org_id}/users` endpoint.
 *
 * @param userId - The ID of the user to grant SuperAdmin access to
 *
 * @throws {ApiError} If the request fails or the user is not found
 *
 * @example
 * ```typescript
 * await grantUserSuperAdmin('user-123');
 * ```
 */
export async function grantUserSuperAdmin(userId: string): Promise<void> {
  await apiClient.post(`/users/${userId}/grant-super-admin`);
}

/**
 * Revoke a user's platform-wide SuperAdmin access.
 *
 * Calls the platform-level endpoint `POST /users/{user_id}/revoke-super-admin`.
 * This is the correct endpoint to use since SuperAdmin is a platform-level concept
 * and is no longer revoked via the org-scoped `DELETE /orgs/{org_id}/users` endpoint.
 *
 * @param userId - The ID of the user to revoke SuperAdmin access from
 *
 * @throws {ApiError} If the request fails or the user is not found
 *
 * @example
 * ```typescript
 * await revokeUserSuperAdmin('user-123');
 * ```
 */
export async function revokeUserSuperAdmin(userId: string): Promise<void> {
  await apiClient.post(`/users/${userId}/revoke-super-admin`);
}

/**
 * Grant a role to a user in a specific organization.
 *
 * @param orgId - The organization ID
 * @param request - The grant access request body (email + roles)
 * @returns Promise resolving to the grant access response
 *
 * @throws {ApiError} If the request fails or user is not found
 *
 * @example
 * ```typescript
 * await grantUserOrgRole('org-123', { email: 'user@example.com', roles: ['OrgAdmin'] });
 * ```
 */
export async function grantUserOrgRole(
  orgId: string,
  request: GrantUserOrgAccessRequest,
): Promise<GrantUserOrgAccessResponse> {
  const result = await apiClient.post<GrantUserOrgAccessResponse>(`/orgs/${orgId}/users`, request);

  if (result === null) {
    throw new Error('Grant role request returned no content');
  }

  return result;
}

/**
 * Revoke a role from a user in a specific organization.
 *
 * @param orgId - The organization ID
 * @param request - The revoke access request body (email + optional roles)
 * @returns Promise resolving to the revoke access response
 *
 * @throws {ApiError} If the request fails or user is not found
 *
 * @example
 * ```typescript
 * // Revoke only the OrgAdmin role
 * await revokeUserOrgRole('org-123', { email: 'user@example.com', roles: ['OrgAdmin'] });
 *
 * // Revoke all roles (removes user from org entirely)
 * await revokeUserOrgRole('org-123', { email: 'user@example.com' });
 * ```
 */
export async function revokeUserOrgRole(
  orgId: string,
  request: RevokeUserOrgAccessRequest,
): Promise<RevokeUserOrgAccessResponse> {
  const result = await apiClient.delete<RevokeUserOrgAccessResponse>(`/orgs/${orgId}/users`, request);

  if (result === null) {
    throw new Error('Revoke role request returned no content');
  }

  return result;
}
